

import { motion, AnimatePresence } from "framer-motion";
import { Xmark } from "iconoir-react";
import { useEffect } from "react";
import { Button } from "@/components/ui/Button";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  tagline: string;
}

export function Modal({ isOpen, onClose, title, description, tagline }: ModalProps) {
  // Prevent scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-charcoal/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative w-full max-w-2xl bg-warm-white rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
          >
            {/* Header / Banner */}
            <div className="bg-mineral p-6 sm:p-8 relative">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 rounded-full bg-warm-white/50 hover:bg-warm-white flex items-center justify-center text-charcoal transition-colors z-10"
              >
                <Xmark width={24} height={24} />
              </button>
              <span className="text-xs uppercase tracking-widest text-amber font-semibold block mb-2">{tagline}</span>
              <h2 className="text-2xl sm:text-3xl font-display font-bold text-charcoal pr-8">{title}</h2>
            </div>
            
            {/* Body */}
            <div className="p-6 sm:p-8 overflow-y-auto">
              <div className="prose prose-lg prose-p:text-graphite prose-p:leading-relaxed max-w-none">
                <p>{description}</p>
                <p>
                  Our methodology ensures that data is not just collected, but transformed into strategic insight.
                  By engaging closely with stakeholders and utilizing advanced operational tools, we guarantee a
                  solution that is both technically robust and institutionally actionable.
                </p>
              </div>
            </div>
            
            {/* Footer */}
            <div className="p-6 sm:p-8 border-t border-mineral/50 bg-mineral/10 flex flex-wrap gap-4 items-center justify-end">
              <Button variant="ghost" onClick={onClose}>Close</Button>
              <Button variant="primary" href="/contact">Contact an Expert</Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
